/**
 * cptLabels — a SMALL, static reference of the standard AMA CPT / HCPCS code meanings for the codes that
 * actually appear on op_fact_encounter.cpt_or_hcpcs_code (E&M office visits + the breast procedures seen
 * in the sample). This is the ONE allowed external lookup: the descriptions are the UNIVERSAL AMA code
 * meanings (not patient data, not pulled from op_*), so every surface that uses them must label them as
 * the standard CPT reference — see CPT_REFERENCE_NOTE. The op_* boundary is preserved: we never invent a
 * description, and an unknown code renders bare (never a fabricated meaning), a null code renders the
 * honest "no CPT recorded" placeholder.
 */

/** Honest placeholder for an encounter with no cpt_or_hcpcs_code recorded (NOT a fake "unknown visit").
 *  A plain hyphen (design-law: no em-dash in rendered copy — use a period/colon/hyphen or a real label). */
export const NO_CPT = "-";

/** Shown next to any rendered CPT label so it is never mistaken for an op_* / billing-derived value. */
export const CPT_REFERENCE_NOTE =
  "CPT descriptions are the standard AMA code meanings, not from op_*.";

/**
 * Standard AMA meanings for the common office-visit (E&M) and breast-procedure codes seen on the visit
 * code column. Descriptions are intentionally concise. Keyed by the bare code as stored in op_*.
 */
export const CPT_LABELS: Record<string, string> = {
  // Evaluation & Management — new patient office/outpatient visit
  "99202": "Office/outpatient visit, new, straightforward",
  "99203": "Office/outpatient visit, new, low-mod",
  "99204": "Office/outpatient visit, new, mod-high",
  "99205": "Office/outpatient visit, new, high",
  // Evaluation & Management — established patient office/outpatient visit
  "99211": "Office/outpatient visit, est, minimal",
  "99212": "Office/outpatient visit, est, straightforward",
  "99213": "Office/outpatient visit, est, low-mod",
  "99214": "Office/outpatient visit, est, mod-high",
  "99215": "Office/outpatient visit, est, high",
  // Breast procedures
  "19316": "Mastopexy (breast lift)",
  "19325": "Breast augmentation with implant",
};

/** The standard AMA description for a code, or null when the code is unknown (no fabricated meaning). */
export function cptDescription(code: string | null | undefined): string | null {
  if (code == null) return null;
  const trimmed = code.trim();
  if (trimmed === "") return null;
  return CPT_LABELS[trimmed] ?? null;
}

/**
 * The display string for a visit code:
 *   - null / empty   -> NO_CPT ("-", honest "no CPT recorded", never a fake)
 *   - known code     -> "{code} · {label}" (standard AMA meaning, see CPT_REFERENCE_NOTE)
 *   - unknown code   -> the bare "{code}" (we never invent a description)
 */
export function cptLabel(code: string | null | undefined): string {
  if (code == null) return NO_CPT;
  const trimmed = code.trim();
  if (trimmed === "") return NO_CPT;
  const desc = CPT_LABELS[trimmed];
  return desc ? `${trimmed} · ${desc}` : trimmed;
}
